import React, { useState, useEffect } from "react";
import { X, Download, Share, ExternalLink } from "lucide-react";
import { PWAInstallationManager } from "../../../../../shared/utils/pwaInstallationContext";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const InstallPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showManualButton, setShowManualButton] = useState(false);
  const [showOpenInApp, setShowOpenInApp] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInSafari, setIsInSafari] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  // When true, show manual "add to home screen" steps instead of the native-install button —
  // used whenever the browser's beforeinstallprompt isn't available (iOS, or Android where the
  // event never fired), so the Install button is never a dead no-op.
  const [manualMode, setManualMode] = useState(false);

  useEffect(() => {
    // Clean up old unscoped localStorage keys that might interfere (ONE TIME only)
    if (!localStorage.getItem('pwa-keys-migrated-whitelabel')) {
      const oldKeys = ['pwa-install-completed', 'pwa-install-dismissed', 'pwa-manual-button-dismissed', 'open-in-app-dismissed'];
      oldKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      });
      localStorage.setItem('pwa-keys-migrated-whitelabel', 'true');
    }

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);

    // Enhanced desktop detection - multiple checks
    const isDesktopOS = /windows nt|macintosh|mac os x|linux x86_64/.test(
      userAgent
    );
    const hasNoMobileKeywords = !/mobile|android|iphone|ipad|ipod/.test(
      userAgent
    );
    const isLargeScreen = window.innerWidth > 1024;
    const isDesktopDevice =
      (isDesktopOS && hasNoMobileKeywords) ||
      (!isIOSDevice && !isAndroidDevice && isLargeScreen);

    const isInSafariBrowser =
      isIOSDevice &&
      /safari/.test(userAgent) &&
      !/chrome|crios|fxios/.test(userAgent);

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    setIsInSafari(isInSafariBrowser);
    setIsDesktop(isDesktopDevice);

    // Don't show anything on desktop - STRICT CHECK
    if (isDesktopDevice) {
      return;
    }

    // Check if already running in PWA mode - if so, don't show ANY prompts
    const isRunningInPWA =
      (window.navigator as any).standalone === true || // iOS PWA
      window.matchMedia("(display-mode: standalone)").matches || // Android PWA
      window.matchMedia("(display-mode: fullscreen)").matches || // Fullscreen PWA
      window.matchMedia("(display-mode: minimal-ui)").matches; // Minimal UI PWA

    if (isRunningInPWA) {
      return; // User is already in PWA mode, don't show any install prompts
    }

    // Check if app is already installed
    const detectAppInstallation = async () => {
      let appInstalled = false;

      // Check if running in standalone mode
      if (
        (window.navigator as any).standalone ||
        window.matchMedia("(display-mode: standalone)").matches
      ) {
        appInstalled = true;
      }

      // Check localStorage for previous install (scoped to app)
      const appDomain = window.location.hostname.includes("localhost")
        ? "whitelabel-dev"
        : "whitelabel-prod";
      const installHistory = localStorage.getItem(`pwa-install-completed-${appDomain}`);
      if (installHistory) {
        const timeSinceInstall = Date.now() - parseInt(installHistory);
        if (timeSinceInstall < 30 * 24 * 60 * 60 * 1000) {
          // 30 days
          appInstalled = true;
        }
      }

      // For Android, use getInstalledRelatedApps if available
      if (isAndroidDevice && "getInstalledRelatedApps" in navigator) {
        try {
          const relatedApps = await (
            navigator as any
          ).getInstalledRelatedApps();
          if (relatedApps.length > 0) {
            appInstalled = true;
          }
        } catch (error) {
          // Feature not supported
        }
      }

      return appInstalled;
    };

    detectAppInstallation().then((installed) => {
      // Only show "Open in App" for Android if app is installed

      // Only show "Open in App" for Android if app is installed
      if (installed && isAndroidDevice) {
        setShowOpenInApp(true);
        return;
      }

      // If app is installed on iOS, don't show any prompts
      if (installed && isIOSDevice) {
        return;
      }

      // Check if we should show install prompt based on installation context
      // Be LESS restrictive - always allow install prompts unless explicitly installed on same domain recently
      const installationContext =
        PWAInstallationManager.getInstallationContext();
      const currentDomain = window.location.hostname;

      let shouldShowInstallPrompt = true;

      if (installationContext) {
        // Only skip if:
        // 1. User installed from EXACTLY this whitelabel domain
        // 2. The installation was recent (within 30 days)
        const isSameDomain = installationContext.installationDomain === currentDomain;
        const isRecent = Date.now() - installationContext.installationTimestamp < 30 * 24 * 60 * 60 * 1000;
        
        // For whitelabel, allow installs:
        // - From different domains/subdomains
        // - If installation is old
        // - Always allow if user came from main app (cross-domain flow)
        shouldShowInstallPrompt = !isSameDomain || !isRecent || installationContext.isMainApp;
      }

      // Even if installation context suggests not showing, check if app is actually installed
      // This handles cases where user uninstalled or cleared storage
      if (!shouldShowInstallPrompt) {
        // Double-check by seeing if we're NOT in PWA mode - if in browser, show prompt
        if (!isRunningInPWA) {
          shouldShowInstallPrompt = true;
        }
      }

      if (!shouldShowInstallPrompt) {
        return;
      }

      // Check if user recently dismissed install prompt
      const appDomain = currentDomain.includes("localhost")
        ? "whitelabel-dev"
        : "whitelabel-prod";
      const installDismissed = localStorage.getItem(
        `pwa-install-dismissed-${appDomain}`
      );
      const now = Date.now();

      if (installDismissed) {
        // Exponential backoff: quiet period doubles per dismissal (1d → 2d → 4d …,
        // capped at 30d); reset on accepted install.
        const dismissCount = parseInt(
          localStorage.getItem(`pwa-install-dismiss-count-${appDomain}`) || "1"
        );
        const backoffMs = Math.min(
          24 * 60 * 60 * 1000 * Math.pow(2, Math.max(dismissCount - 1, 0)),
          30 * 24 * 60 * 60 * 1000
        );
        if (now - parseInt(installDismissed) < backoffMs) {
          return;
        }
      }

      // Show appropriate prompt based on platform with industry-standard delays
      if (isAndroidDevice) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 8000); // 8 seconds - better engagement window
      } else if (isIOSDevice && isInSafariBrowser) {
        setTimeout(() => {
          setShowPrompt(true);
        }, 12000); // 12 seconds for iOS - allows page interaction first
      }
    });

    // Listen for beforeinstallprompt event (Android). Chrome only fires this when the
    // app is NOT currently installed — it's the authoritative uninstall signal. Our
    // pwa-install-completed-* localStorage flag survives an uninstall (30-day
    // suppression), which used to leave uninstalled users with a dead "Open in App"
    // button and no way to reinstall. Trust the browser: clear the stale flag and offer
    // the install again.
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      const appDomain = window.location.hostname.includes("localhost")
        ? "whitelabel-dev"
        : "whitelabel-prod";
      localStorage.removeItem(`pwa-install-completed-${appDomain}`);
      setShowOpenInApp(false);
      setDeferredPrompt(e);

      // Respect the dismissal backoff — bip fires on every eligible visit, and without
      // this check a user who said "Later" would be re-prompted immediately.
      const dismissed = localStorage.getItem(`pwa-install-dismissed-${appDomain}`);
      if (dismissed) {
        const dismissCount = parseInt(
          localStorage.getItem(`pwa-install-dismiss-count-${appDomain}`) || "1"
        );
        const backoffMs = Math.min(
          24 * 60 * 60 * 1000 * Math.pow(2, Math.max(dismissCount - 1, 0)),
          30 * 24 * 60 * 60 * 1000
        );
        if (Date.now() - parseInt(dismissed) < backoffMs) {
          return; // Keep deferredPrompt so a manual install button still works.
        }
      }
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // iOS can't install programmatically — show the manual steps instead of no-op.
      localStorage.setItem("ios-install-attempted", Date.now().toString());
      setManualMode(true);
      return;
    }

    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;

        if (choice.outcome === "accepted") {
          // Record successful installation with better subdomain handling
          const currentDomain = window.location.hostname;
          let affiliationId = "whitelabel";
          let affiliationName = "Community";

          if (
            currentDomain.endsWith(".zurilink.com") &&
            currentDomain !== "zurilink.com"
          ) {
            // Production subdomain - extract subdomain name
            affiliationId = currentDomain.replace(".zurilink.com", "");
            affiliationName =
              affiliationId.charAt(0).toUpperCase() + affiliationId.slice(1);
          } else if (currentDomain.includes("localhost:3001")) {
            // Development subdomain
            affiliationId = "whitelabel-dev";
            affiliationName = "Community (Dev)";
          }

          PWAInstallationManager.recordInstallation({
            domain: currentDomain,
            affiliationId,
            affiliationName,
          });

          const appDomain = window.location.hostname.includes("localhost")
            ? "whitelabel-dev"
            : "whitelabel-prod";
          localStorage.setItem(`pwa-install-completed-${appDomain}`, Date.now().toString());
          // Accepted install resets the dismissal backoff clock.
          localStorage.removeItem(`pwa-install-dismiss-count-${appDomain}`);
          localStorage.removeItem(`pwa-install-dismissed-${appDomain}`);

          // IMPORTANT: Immediately hide all prompts and clear deferred prompt
          setShowPrompt(false);
          setShowManualButton(false);
          setDeferredPrompt(null);
        } else {
          // User dismissed - also clear the deferred prompt
          setDeferredPrompt(null);
        }
      } catch (error) {
        console.error("Error during install prompt:", error);
        setDeferredPrompt(null); // Clear on error too
        setManualMode(true); // fall back to manual steps rather than a dead button
      }
      return;
    }

    // No native install prompt available (Android where beforeinstallprompt never fired,
    // or a browser that installs via its own menu) — show manual steps, never a no-op.
    setManualMode(true);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowManualButton(false);
    setShowOpenInApp(false);

    // Record dismissal — exponential backoff: each dismissal doubles the quiet period
    // (1d → 2d → 4d …, capped at 30d); an accepted install resets the count.
    const currentDomain = window.location.hostname;
    const appDomain = currentDomain.includes("localhost")
      ? "whitelabel-dev"
      : "whitelabel-prod";
    localStorage.setItem(
      `pwa-install-dismissed-${appDomain}`,
      Date.now().toString()
    );
    const count =
      parseInt(
        localStorage.getItem(`pwa-install-dismiss-count-${appDomain}`) || "0"
      ) + 1;
    localStorage.setItem(
      `pwa-install-dismiss-count-${appDomain}`,
      count.toString()
    );

    // Show manual button after dismissing for Android (industry standard fallback)
    if (isAndroid) {
      setTimeout(() => {
        setShowManualButton(true);
      }, 30000); // Show after 30 seconds
    }
  };

  const handleManualButtonDismiss = () => {
    setShowManualButton(false);
    const currentDomain = window.location.hostname;
    const appDomain = currentDomain.includes("localhost")
      ? "whitelabel-dev"
      : "whitelabel-prod";
    localStorage.setItem(
      `pwa-manual-button-dismissed-${appDomain}`,
      Date.now().toString()
    );
  };

  const handleManualInstall = () => {
    // Surface the platform-appropriate "add to home screen" steps.
    setShowManualButton(false);
    setManualMode(true);
    setShowPrompt(true);
  };

  // Don't show anything on desktop - only iOS and Android
  if (isDesktop) {
    return null;
  }

  // Don't render anything if no prompts should be shown
  if (!showPrompt && !showManualButton && !showOpenInApp) {
    return null;
  }

  // Minimal manual install button - only Android
  if (showManualButton && !showPrompt && !showOpenInApp && isAndroid) {
    return (
      <div className="fixed bottom-4 right-4 z-40">
        <div className="relative">
          <button
            onClick={handleManualInstall}
            className="bg-white rounded-full shadow-md border border-gray-200 p-2.5 hover:shadow-lg transition-shadow duration-200"
            title="Install app"
          >
            <Download className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={handleManualButtonDismiss}
            className="absolute -top-1 -right-1 bg-gray-300 text-gray-600 rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-gray-400 transition-colors"
            title="Hide"
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 animate-slide-up">
        {/* Install Prompt for Android */}
        {showPrompt && (
          <>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Download className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900">
                  Install App
                </h3>
                {(isIOS && isInSafari) || (manualMode && isIOS) ? (
                  <div className="text-sm text-gray-600 mt-2 space-y-2">
                    <p className="font-medium">How to install:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Tap the <Share className="w-3 h-3 inline mx-1" /> Share button below</li>
                      <li>Scroll and tap "Add to Home Screen"</li>
                      <li>Tap "Add" to install</li>
                    </ol>
                  </div>
                ) : manualMode ? (
                  <div className="text-sm text-gray-600 mt-2 space-y-2">
                    <p className="font-medium">How to install:</p>
                    <ol className="list-decimal list-inside space-y-1 text-xs">
                      <li>Open your browser's menu (⋮ in the top-right)</li>
                      <li>Tap "Install app" or "Add to Home screen"</li>
                      <li>Confirm to add it to your home screen</li>
                    </ol>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 mt-1">
                    Install this app on your device for a better experience and offline access.
                  </p>
                )}
              </div>
              <button
                onClick={handleDismiss}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Once we're showing manual steps (iOS, or Android with no native prompt),
                there's nothing to auto-trigger — just a dismiss. Otherwise the Install
                button tries the native prompt and falls back to manual steps if it can't. */}
            {(isIOS && isInSafari) || manualMode ? (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleDismiss}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Got it
                </button>
              </div>
            ) : (
              // Android: Show install and later buttons
              <div className="mt-4 flex space-x-2">
                <button
                  onClick={handleInstallClick}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  <Download className="w-4 h-4 inline mr-2" />
                  Install
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Later
                </button>
              </div>
            )}
          </>
        )}

        {/* Manual Install for iOS Safari */}
        {showManualButton && (
          <>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Share className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900">
                  Add to Home Screen
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Tap the share button and select "Add to Home Screen" to
                  install this app.
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-4 flex space-x-2">
              <button
                onClick={handleManualInstall}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <Share className="w-4 h-4 mr-2" />
                Show Instructions
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Later
              </button>
            </div>
          </>
        )}

        {/* Open in App notification - PWA is already installed */}
        {showOpenInApp && (
          <>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <ExternalLink className="w-4 h-4 text-green-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900">
                  App Installed!
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  For the best experience, open this app from your home screen.
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Got it
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
